import { AuthBrowser } from "./AuthBrowser";
import { AuthNode } from "./AuthNode";
/**
 * Factory for creating authentication services
 */
export class AuthFactory {
    /**
     * Create a browser authentication service
     *
     * Required environment variables:
     * - VUE_APP_CSA_SSO_CLIENT_ID: The client ID for authentication
     * - VUE_APP_CSA_SSO_CLIENT_SECRET: The client secret for authentication
     * - VUE_APP_CSA_SSO_REGION: The region for authentication
     * - VUE_APP_CSA_SSO_GRANT_TYPE: The grant type for authentication (usually "password")
     * - VUE_APP_CSA_SSO_SCOPE: The scope for authentication
     * - VUE_APP_CSA_SSO_URL: The SSO URL for authentication
     *
     * For auto sign-in:
     * - VUE_APP_CSA_USERNAME: The username for auto sign-in
     * - VUE_APP_CSA_PASSWORD: The password for auto sign-in
     *
     * @param fetchImpl - The fetch implementation to use
     * @returns A browser authentication service
     */
    static createBrowser(fetchImpl) {
        // Use browser's native fetch if not provided
        if (!fetchImpl && typeof window !== "undefined" && window.fetch) {
            fetchImpl = window.fetch.bind(window);
        }
        if (!fetchImpl) {
            throw new Error("No fetch implementation provided. In browser environments, " +
                "window.fetch should be available.");
        }
        return new AuthBrowser({
            clientId: process.env.VUE_APP_CSA_SSO_CLIENT_ID || "",
            clientSecret: process.env.VUE_APP_CSA_SSO_CLIENT_SECRET || "",
            region: process.env.VUE_APP_CSA_SSO_REGION || "",
            grantType: process.env.VUE_APP_CSA_SSO_GRANT_TYPE || "password",
            scope: process.env.VUE_APP_CSA_SSO_SCOPE || "",
            ssoUrl: process.env.VUE_APP_CSA_SSO_URL || "",
        }, fetchImpl);
    }
    /**
     * Create a Node.js authentication service
     *
     * Required environment variables:
     * - API_SSO_CLIENT_ID: The client ID for authentication
     * - API_SSO_CLIENT_SECRET: The client secret for authentication
     * - API_SSO_REGION: The region for authentication
     * - API_SSO_GRANT_TYPE: The grant type for authentication (usually "password")
     * - API_SSO_SCOPE: The scope for authentication
     * - API_SSO_URL: The SSO URL for authentication
     * - API_USERNAME: The username for service account
     * - API_PASSWORD: The password for service account
     *
     * @param config - Configuration options
     * @param fetchImpl - The fetch implementation to use (required in Node.js)
     * @returns A Node.js authentication service
     */
    static createNode(config, fetchImpl) {
        if (!fetchImpl) {
            throw new Error("No fetch implementation provided. In Node.js environments, " +
                "you must provide a fetch implementation (e.g., node-fetch).");
        }
        // Get configuration from environment variables or provided config
        const clientId = config?.clientId || process.env.CSA_SSO_CLIENT_ID || "";
        const clientSecret = config?.clientSecret || process.env.CSA_SSO_CLIENT_SECRET || "";
        const region = config?.region || process.env.CSA_SSO_REGION || "";
        const grantType = config?.grantType || process.env.CSA_SSO_GRANT_TYPE || "password";
        const scope = config?.scope || process.env.CSA_SSO_SCOPE || "";
        const ssoUrl = config?.ssoUrl || process.env.CSA_SSO_URL || "";
        const username = config?.username || process.env.CSA_USERNAME || "";
        const password = config?.password || process.env.CSA_PASSWORD || "";
        return new AuthNode({
            clientId,
            clientSecret,
            region,
            grantType,
            scope,
            ssoUrl,
            username,
            password,
        }, fetchImpl);
    }
    /**
     * Create the appropriate authentication service for the current environment
     * @param options - Options for creating the authentication service
     * @param fetchImpl - The fetch implementation to use (required in Node.js)
     * @returns An authentication service
     */
    static create(options = {}, fetchImpl) {
        // Check if we're in a browser environment
        const isBrowser = typeof window !== "undefined";
        if (isBrowser) {
            return AuthFactory.createBrowser(fetchImpl);
        }
        else {
            return AuthFactory.createNode(options.node, fetchImpl);
        }
    }
}
